import React, {useRef, useState} from 'react';
import {Button, Card, message, Modal, Space, Tag, Tooltip, Typography} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    MenuOutlined,
    PlusOutlined,
    SearchOutlined,
    WarningOutlined
} from '@ant-design/icons';
import type {ActionType, ProColumns} from '@ant-design/pro-components';
import {DragSortTable} from '@ant-design/pro-components';
import axiosApiInstance from '../../../api/axiosClient';
import TrackEditorModal from './TrackEditorModal';
import TrackSearchModal from './TrackSearchModal';
import {releaseInfoFormAtom, useReleaseInfoForm} from './release_form_atoms';
import {useAtom, useAtomValue} from 'jotai';
import {buildNewTrackAutofill, resolveReleaseInfoForTrackAutofillAsync} from './trackAutofill';

const {Title} = Typography;

interface SortableTrackListProps {
    volumeNumber: number;
    volumeTracks: API.MusicDistribution.ArtTrack[];
    releaseId: string;
    onTracksChange: (updatedTracks: API.MusicDistribution.ArtTrack[]) => void;
    onRemoveVolume?: () => void;
    otherVolumes: { label: string; value: number }[];
}

const SortableTrackList: React.FC<SortableTrackListProps> = ({
                                                                 volumeNumber,
                                                                 volumeTracks,
                                                                 releaseId,
                                                                 onTracksChange,
                                                                 onRemoveVolume,
                                                                 otherVolumes
                                                             }) => {
    const [editingTrack, setEditingTrack] = useState<API.MusicDistribution.ArtTrack | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isNewTrack, setIsNewTrack] = useState(false);
    const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [trackToDelete, setTrackToDelete] = useState<API.MusicDistribution.ArtTrack | null>(null);
    const actionRef = useRef<ActionType>();
    const releaseInfoForm = useAtomValue(useReleaseInfoForm);
    const [releaseInfoData] = useAtom(releaseInfoFormAtom);

    // Add a new track
    const handleAddTrack = async () => {
        const releaseInfo = await resolveReleaseInfoForTrackAutofillAsync(
            releaseInfoForm?.getFieldsValue?.() ?? {},
            releaseInfoData ?? {},
            releaseId,
        );
        const {title, main_artists} = buildNewTrackAutofill(releaseInfo);

        setIsNewTrack(true);
        setEditingTrack({
            id: '',
            isrc: '',
            title,
            subtitle: '',
            title_language: 'el',
            track_number: volumeTracks.length + 1,
            volume_number: volumeNumber,
            lyrics_language: 'el',
            parental_advisory: 'No',
            territories: 'WW',
            main_artists,
            featuring_artists: [],
            composers: [],
            authors: [],
            remixers: [],
            producers: [],
            arrangers: [],
            publishers: [],
            // products: [],
            track_type: 'ORIGINAL',
            release_price_tier: '',
            track_price_tier: '',
            preview_start: 45,
            is_instrumental: false,
            is_uploaded: false, // only for backwards compatibility
            filename: '',
            audio_file_release_id: releaseId,
            duration: 0,
        });
        setIsModalVisible(true);
    };

    // Edit a track
    const handleEditTrack = (track: API.MusicDistribution.ArtTrack) => {
        setIsNewTrack(false);
        setEditingTrack(track);
        setIsModalVisible(true);
    };

    // Handle selecting a track from search results
    const handleSelectTrack = async (track: API.MusicDistribution.ArtTrack) => {
        try {
            // Create a copy of the track with updated volume and track number
            const trackWithPlacement = {
                ...track,
                volume_number: volumeNumber,
                track_number: volumeTracks.length + 1
            };

            // Associate the track with the current release
            await axiosApiInstance.put('/api/products/art_tracks/placement', {
                track_id: track.id,
                release_id: releaseId,
                track_isrc: track.isrc,
                volume_number: trackWithPlacement.volume_number,
                track_number: trackWithPlacement.track_number
            });

            // Add the track to the current volume
            const updatedTracks = [...volumeTracks, trackWithPlacement];
            onTracksChange(updatedTracks);

            message.success('Track added successfully');
        } catch (error) {
            console.error('Error adding track:', error);
            message.error('Failed to add track');
        }
    };

    // Show delete confirmation with options
    const showDeleteConfirmation = (track: API.MusicDistribution.ArtTrack) => {
        setTrackToDelete(track);
        setIsDeleteModalVisible(true);
    };

    // Delete a track association from release (not the actual track)
    const handleDeleteTrack = async (track: API.MusicDistribution.ArtTrack) => {
        try {
            // Get the track number of the track to be deleted
            const deletedTrackNumber = track.track_number;

            // Create a copy of the tracks without the deleted track
            const updatedTracks = volumeTracks.filter(t => t.id !== track.id);

            // Update track numbers for tracks after the deleted one
            const tracksToUpdate = updatedTracks.filter(t => t.track_number > deletedTrackNumber);

            // Delete the track placement using the dedicated endpoint
            await axiosApiInstance.delete('/api/products/art_tracks/placement', {
                data: {
                    track_id: track.id,
                    release_id: releaseId,
                    volume_number: volumeNumber
                }
            });

            // Update track numbers for tracks after the deleted one
            for (const t of tracksToUpdate) {
                // Decrement the track number
                t.track_number -= 1;

                // Update the track placement in the backend
                await axiosApiInstance.put('/api/products/art_tracks/placement', {
                    track_id: t.id,
                    release_id: releaseId,
                    track_isrc: t.isrc,
                    volume_number: volumeNumber,
                    track_number: t.track_number
                });
            }

            // Notify parent component of the change
            onTracksChange(updatedTracks);

            message.success('Track removed from release successfully');
        } catch (error) {
            console.error('Error removing track from release:', error);
            message.error('Failed to remove track from release');
        }
    };

    // Delete a track completely from the system
    const handleDeleteTrackCompletely = async (track: API.MusicDistribution.ArtTrack) => {
        try {
            // Delete the track completely using the dedicated endpoint
            await axiosApiInstance.delete('/api/products/art_tracks/completely', {
                data: {
                    track_id: track.id
                }
            });

            // Create a copy of the tracks without the deleted track
            const updatedTracks = volumeTracks.filter(t => t.id !== track.id);

            // Update track numbers for all remaining tracks
            for (let i = 0; i < updatedTracks.length; i++) {
                updatedTracks[i].track_number = i + 1;

                // Update the track placement in the backend
                await axiosApiInstance.put('/api/products/art_tracks/placement', {
                    track_id: updatedTracks[i].id,
                    release_id: releaseId,
                    track_isrc: updatedTracks[i].isrc,
                    volume_number: volumeNumber,
                    track_number: updatedTracks[i].track_number
                });
            }

            // Notify parent component of the change
            onTracksChange(updatedTracks);

            message.success('Track deleted completely');
        } catch (error) {
            console.error('Error deleting track completely:', error);

            // Check if the error is because the track is used in multiple releases
            if (error.response && error.response.status === 403) {
                message.error('Cannot delete track as it is used in multiple releases');
            } else {
                message.error('Failed to delete track completely');
            }
        }
    };

    // Handle track save from modal
    const handleTrackSave = (savedTrack: API.MusicDistribution.ArtTrack) => {
        // Ensure the track has the correct volume number
        savedTrack.volume_number = volumeNumber;

        if (isNewTrack) {
            // Add new track
            savedTrack.track_number = volumeTracks.length + 1;
            const updatedTracks = [...volumeTracks, savedTrack];
            onTracksChange(updatedTracks);
        } else {
            // Update existing track
            const updatedTracks = volumeTracks.map(track =>
                track.id === savedTrack.id ? savedTrack : track
            );
            onTracksChange(updatedTracks);
        }
    };

    // Handle drag sort end
    const handleDragSortEnd = async (
        // beforeIndex: number,
        // afterIndex: number,
        newDataSource: API.MusicDistribution.ArtTrack[]
    ) => {
        console.log('Drag sort end:', /*beforeIndex, afterIndex,*/ newDataSource);
        try {
            // Update the track numbers in the new data source
            const updatedDataSource = newDataSource.map((track, index) => ({
                ...track,
                track_number: index + 1
            }));

            // Update backend for each track to ensure correct placement
            for (let i = 0; i < updatedDataSource.length; i++) {
                const track = updatedDataSource[i];
                await axiosApiInstance.put('/api/products/art_tracks/placement', {
                    track_id: track.id,
                    release_id: releaseId,
                    track_isrc: track.isrc,
                    volume_number: volumeNumber,
                    track_number: i + 1
                });
            }

            // Notify parent component of the change
            onTracksChange(updatedDataSource);

            message.success('Track order updated successfully');
        } catch (error) {
            console.error('Error updating track placement:', error);
            message.error('Failed to update track order');
        }
    };

    // Move track to another volume
    const moveTrackToVolume = async (track: API.MusicDistribution.ArtTrack, targetVolumeNumber: number) => {
        try {
            // Get the track number of the track to be moved
            const movedTrackNumber = track.track_number;

            // Step 1: Get the release data to find tracks in the target volume
            const response = await axiosApiInstance.get(`/api/products/releases/${releaseId}`);
            const releaseData = response.data;

            // Filter tracks in the target volume
            const targetVolumeTracks = releaseData.art_tracks.filter(
                (t: API.MusicDistribution.ArtTrack) => t.volume_number === targetVolumeNumber
            );

            // Find the highest track number in the target volume
            const highestTrackNumber = targetVolumeTracks.length > 0
                ? Math.max(...targetVolumeTracks.map((t: API.MusicDistribution.ArtTrack) => t.track_number))
                : 0;

            // Step 2: Update the track placement to the end of the target volume
            await axiosApiInstance.put('/api/products/art_tracks/placement', {
                track_id: track.id,
                release_id: releaseId,
                track_isrc: track.isrc,
                volume_number: targetVolumeNumber,
                track_number: highestTrackNumber + 1 // Place at the end of the target volume
            });

            // Step 3: Remove track from current volume
            const updatedTracks = volumeTracks.filter(t => t.id !== track.id);

            // Step 4: Update track numbers for tracks after the moved one in the source volume
            const tracksToUpdate = updatedTracks.filter(t => t.track_number > movedTrackNumber);

            // Update track numbers in the backend for the source volume
            for (const t of tracksToUpdate) {
                // Decrement the track number
                t.track_number -= 1;

                // Update the track placement in the backend
                await axiosApiInstance.put('/api/products/art_tracks/placement', {
                    track_id: t.id,
                    release_id: releaseId,
                    track_isrc: t.isrc,
                    volume_number: volumeNumber,
                    track_number: t.track_number
                });
            }

            // Notify parent component of the change
            onTracksChange(updatedTracks);

            message.success('Track moved to another volume successfully');
        } catch (error) {
            console.error('Error moving track to another volume:', error);
            message.error('Failed to move track to another volume');
        }
    };

    // Generate columns for the DragSortTable
    const getColumns = (): ProColumns<API.MusicDistribution.ArtTrack>[] => {
        return [
            {
                title: 'Track #',
                dataIndex: 'track_number',
                width: 90,
                className: 'drag-visible',
            },

            {
                title: 'Title',
                dataIndex: 'title',
                className: 'drag-visible',
            },
            {
                title: 'Artists',
                dataIndex: 'main_artists',
                render: (_, record) => {
                    const artistNames = record.main_artists?.map(a => a.key_name).join(', ') || '';
                    return <span>{artistNames}</span>;
                },
            },
            {
                title: 'ISRC',
                dataIndex: 'isrc',
            },
            {
                title: 'Filename',
                dataIndex: 'filename',
                render: (filename) => filename || '-',
            },
            {
                title: 'Audio Source',
                dataIndex: 'audio_file_release_id',
                key: 'audioSource',
                render: (audioFileReleaseId: string, record: API.MusicDistribution.ArtTrack) => {
                    console.log({ audioFileReleaseId, releaseId, record });
                    if (record.audio_file_release_id === releaseId) {
                        return <Tag color="green">This Release</Tag>;
                    }
                    if (!record.audio_file_release_id) {
                        return audioFileReleaseId // a dash as handled by antd
                    }
                    return <Tag color="orange">Other Release</Tag>;
                },
            },
            {
                title: 'Status',
                dataIndex: 'validation_warnings',
                width: 80,
                render: (_, record) => {
                    if (!record.validation_warnings || record.validation_warnings.length === 0) {
                        return null;
                    }

                    const warningCount = record.validation_warnings.length;

                    const tooltipContent = (
                        <div>
                            <div><strong>{warningCount} warning{warningCount !== 1 ? 's' : ''}</strong></div>
                            {/*<ul style={{margin: '5px 0 0 0', padding: '0 0 0 20px'}}>*/}
                            {/*    {record.validation_warnings.map((warning, index) => (*/}
                            {/*        <li key={index}><Typography.Text type="warning">{warning.message}</Typography.Text>*/}
                            {/*        </li>*/}
                            {/*    ))}*/}
                            {/*</ul>*/}
                            <div style={{marginTop: 5}}>Click to edit track for details</div>
                        </div>
                    );

                    return (
                        <Tooltip
                            title={tooltipContent}/*  destroyTooltipOnHide={true}  overlayStyle={{ width: '300px' }}*/>
                            <Tag
                                color="warning"
                                icon={<WarningOutlined/>}
                                style={{cursor: 'pointer'}}
                                onClick={() => handleEditTrack(record)}
                            >
                                {warningCount}
                            </Tag>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Actions',
                valueType: 'option',
                width: 200,
                render: (_, record) => [
                    <Button
                        key="edit"
                        type="link"
                        icon={<EditOutlined/>}
                        onClick={() => handleEditTrack(record)}
                    >
                        Edit
                    </Button>,
                    <Button
                        key="delete"
                        type="link"
                        danger
                        icon={<DeleteOutlined/>}
                        onClick={() => showDeleteConfirmation(record)}
                    >
                        Delete
                    </Button>,
                    otherVolumes.length > 0 && (
                        <Space key="move" direction="vertical" style={{width: '100%'}}>
                            {otherVolumes.map(volume => (
                                <Button
                                    key={`move-to-${volume.value}`}
                                    type="link"
                                    size="small"
                                    onClick={() => moveTrackToVolume(record, volume.value)}
                                >
                                    Move to Volume {volume.value}
                                </Button>
                            ))}
                        </Space>
                    ),
                ],
            },
        ];
    };

    // Custom drag handle renderer
    const dragHandleRender = (rowData: any, idx: number) => (
        <Space>
            <MenuOutlined style={{cursor: 'grab'}}/>
            {rowData.track_number}
        </Space>
    );

    // Sort tracks by track_number
    const sortedTracks = [...volumeTracks].sort((a, b) => a.track_number - b.track_number);

    return (
        <Card
            title={`Volume ${volumeNumber}`}
            extra={
                <Space>
                    <Button
                        type="primary"
                        icon={<SearchOutlined/>}
                        onClick={() => setIsSearchModalVisible(true)}
                    >
                        Search Existing Tracks
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={handleAddTrack}
                    >
                        Add Track
                    </Button>
                    {volumeTracks.length === 0 && onRemoveVolume && (
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined/>}
                            onClick={onRemoveVolume}
                        >
                            Remove Volume
                        </Button>
                    )}
                </Space>
            }
            style={{marginBottom: 16}}
        >
            <DragSortTable<API.MusicDistribution.ArtTrack>
                // headerTitle={`Volume ${volumeNumber} Tracks`}
                columns={getColumns()}
                rowKey="id"
                search={false}
                pagination={false}
                dataSource={sortedTracks}
                // dragSortKey="sort"
                dragSortKey="track_number"
                dragSortHandlerRender={dragHandleRender}
                onDragSortEnd={handleDragSortEnd}
                actionRef={actionRef}
                toolBarRender={false}
            />

            <TrackEditorModal
                visible={isModalVisible}
                track={editingTrack}
                onClose={() => {
                    setIsModalVisible(false);
                    setEditingTrack(null);
                }}
                onSave={handleTrackSave}
                isNew={isNewTrack}
                releaseId={releaseId}
                isFromOtherRelease={editingTrack?.audio_file_release_id && editingTrack.audio_file_release_id !== releaseId}
                releaseGenre={releaseInfoData?.genre}
                releaseSubGenre={releaseInfoData?.sub_genre}
            />

            <TrackSearchModal
                visible={isSearchModalVisible}
                onClose={() => setIsSearchModalVisible(false)}
                onSelectTrack={handleSelectTrack}
                releaseId={releaseId}
            />

            {/* Delete Confirmation Modal */}
            {trackToDelete && (
                <Modal
                    title="Delete Track"
                    open={isDeleteModalVisible}
                    onCancel={() => setIsDeleteModalVisible(false)}
                    footer={null}
                >
                    <div>
                        <p>How would you like to delete this track?</p>
                        <ul>
                            <li><strong>Remove from release:</strong> Keeps the track in the system but removes it from
                                this release.
                            </li>
                            <li><strong>Delete completely:</strong> Permanently deletes the track from the system.
                                {trackToDelete.audio_file_release_id && trackToDelete.audio_file_release_id !== releaseId && (
                                    <span
                                        style={{color: 'red'}}> (Not available - track is used in another release)</span>
                                )}
                            </li>
                        </ul>
                    </div>
                    <div style={{marginTop: 24, textAlign: 'right'}}>
                        <Space>
                            <Button onClick={() => setIsDeleteModalVisible(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => {
                                    handleDeleteTrack(trackToDelete);
                                    setIsDeleteModalVisible(false);
                                }}
                            >
                                Remove from release
                            </Button>
                            <Button
                                danger
                                disabled={trackToDelete.audio_file_release_id && trackToDelete.audio_file_release_id !== releaseId}
                                onClick={() => {
                                    handleDeleteTrackCompletely(trackToDelete);
                                    setIsDeleteModalVisible(false);
                                }}
                            >
                                Delete completely
                            </Button>
                        </Space>
                    </div>
                </Modal>
            )}
        </Card>
    );
};

export default SortableTrackList;